/**
 * Point counter for buzzer games (Xəmsə-style).
 *
 * Pure state machine — no React, no SignalR. The buzzer integrates with it
 * by feeding verdicts for whoever pressed; anything else (a manual scoreboard,
 * another game screen) can drive it the same way.
 *
 * Rules:
 * - A round has 5 questions worth 10, 20, 30, 40, 50 points; after the 5th the
 *   counter wraps back to the first question value.
 * - The moderator picks the level: standard ×1, double ×2, triple ×3, quadruple ×4.
 * - A correct answer adds the question value to the player and moves on to the
 *   next question; a wrong answer subtracts the same value and leaves the
 *   question open for others.
 * - Each player gets at most one verdict per question.
 * - The level can be locked so it is not changed by a stray click mid-game.
 * - Resetting clears the points only (undoable); level and question stay put.
 */

export type ScoreLevel = 1 | 2 | 3 | 4;
export type Verdict = 'correct' | 'wrong';

export const SCORE_LEVELS: ScoreLevel[] = [1, 2, 3, 4];
export const QUESTIONS_PER_ROUND = 5;
export const BASE_POINTS = 10;
const UNDO_LIMIT = 100;

export const SCORE_LEVEL_LABELS: Record<ScoreLevel, string> = {
  1: 'Standart',
  2: 'İkiqat',
  3: 'Üçqat',
  4: 'Dördqat',
};

export interface ScoreEntry {
  playerName: string;
  verdict: Verdict;
  /** Signed: positive for a correct answer, negative for a wrong one. */
  points: number;
}

export interface PlayerScore {
  name: string;
  points: number;
}

interface ScoringSnapshot {
  level: ScoreLevel;
  /** 1-based position inside the round (1..QUESTIONS_PER_ROUND). */
  questionNo: number;
  /** Keyed by the case-insensitive player name, like the buzzer compares names. */
  players: Record<string, PlayerScore>;
  /** Verdicts given on the current question. */
  verdicts: ScoreEntry[];
  /** Most recent verdict, kept across questions for UI feedback. */
  lastEntry: ScoreEntry | null;
}

export interface ScoringState extends ScoringSnapshot {
  /** While true, setLevel is ignored. A UI safety switch, so undo never flips it. */
  levelLocked: boolean;
  past: ScoringSnapshot[];
}

export type ScoringAction =
  | { type: 'judge'; playerName: string; verdict: Verdict }
  | { type: 'skipQuestion' }
  | { type: 'setQuestion'; questionNo: number }
  | { type: 'setLevel'; level: ScoreLevel }
  | { type: 'setLevelLocked'; locked: boolean }
  | { type: 'adjust'; playerName: string; points: number }
  | { type: 'removePlayer'; playerName: string }
  | { type: 'resetScores' }
  | { type: 'undo' };

const nameKey = (name: string) => name.trim().toLowerCase();

export const questionValue = (level: ScoreLevel, questionNo: number) => BASE_POINTS * questionNo * level;

export const roundValues = (level: ScoreLevel) =>
  Array.from({ length: QUESTIONS_PER_ROUND }, (_, i) => questionValue(level, i + 1));

export const createScoringState = (): ScoringState => ({
  level: 1,
  questionNo: 1,
  players: {},
  verdicts: [],
  lastEntry: null,
  levelLocked: true,
  past: [],
});

export const currentValue = (state: ScoringState) => questionValue(state.level, state.questionNo);

export const getVerdict = (state: ScoringState, playerName: string): ScoreEntry | null =>
  state.verdicts.find((v) => nameKey(v.playerName) === nameKey(playerName)) ?? null;

/** First player in buzz order who has not been judged on the current question. */
export const nextToJudge = (state: ScoringState, pressOrder: string[]): string | null =>
  pressOrder.find((name) => !getVerdict(state, name)) ?? null;

/** Everyone on the roster (at 0 if unscored) plus anyone who scored and left, best first. */
export const getStandings = (state: ScoringState, roster: string[] = []): PlayerScore[] => {
  const merged: Record<string, PlayerScore> = { ...state.players };
  roster.forEach((name) => {
    const key = nameKey(name);
    if (key && !merged[key]) merged[key] = { name, points: 0 };
  });

  return Object.values(merged).sort((a, b) => b.points - a.points || a.name.localeCompare(b.name));
};

const nextQuestionNo = (questionNo: number) => (questionNo % QUESTIONS_PER_ROUND) + 1;

const clampQuestionNo = (questionNo: number) =>
  Math.min(QUESTIONS_PER_ROUND, Math.max(1, Math.round(questionNo)));

const withHistory = (state: ScoringState, next: ScoringSnapshot): ScoringState => {
  const { past, levelLocked, ...snapshot } = state;
  return { ...next, levelLocked, past: [...past, snapshot].slice(-UNDO_LIMIT) };
};

export const scoringReducer = (state: ScoringState, action: ScoringAction): ScoringState => {
  switch (action.type) {
    case 'judge': {
      const key = nameKey(action.playerName);
      if (!key || getVerdict(state, action.playerName)) return state;

      const value = currentValue(state);
      const points = action.verdict === 'correct' ? value : -value;
      const player = state.players[key] ?? { name: action.playerName.trim(), points: 0 };
      const entry: ScoreEntry = { playerName: player.name, verdict: action.verdict, points };
      const players = { ...state.players, [key]: { ...player, points: player.points + points } };

      if (action.verdict === 'correct') {
        return withHistory(state, {
          ...state,
          players,
          questionNo: nextQuestionNo(state.questionNo),
          verdicts: [],
          lastEntry: entry,
        });
      }

      return withHistory(state, {
        ...state,
        players,
        verdicts: [...state.verdicts, entry],
        lastEntry: entry,
      });
    }

    case 'skipQuestion':
      return withHistory(state, { ...state, questionNo: nextQuestionNo(state.questionNo), verdicts: [] });

    case 'setQuestion': {
      const questionNo = clampQuestionNo(action.questionNo);
      if (questionNo === state.questionNo) return state;
      return withHistory(state, { ...state, questionNo, verdicts: [] });
    }

    case 'setLevel':
      if (state.levelLocked || action.level === state.level) return state;
      // A new level starts a fresh round from the cheapest question.
      return withHistory(state, { ...state, level: action.level, questionNo: 1, verdicts: [] });

    case 'setLevelLocked':
      return action.locked === state.levelLocked ? state : { ...state, levelLocked: action.locked };

    case 'adjust': {
      const key = nameKey(action.playerName);
      const delta = Math.round(action.points);
      if (!key || !delta) return state;

      const player = state.players[key] ?? { name: action.playerName.trim(), points: 0 };
      return withHistory(state, {
        ...state,
        players: { ...state.players, [key]: { ...player, points: player.points + delta } },
      });
    }

    case 'removePlayer': {
      // Drops the player's points and any verdict on the current question (undoable).
      const key = nameKey(action.playerName);
      if (!state.players[key] && !getVerdict(state, action.playerName)) return state;
      const players = { ...state.players };
      delete players[key];
      return withHistory(state, {
        ...state,
        players,
        verdicts: state.verdicts.filter((v) => nameKey(v.playerName) !== key),
      });
    }

    case 'resetScores':
      // Points only — the level, the current question and the lock stay as they are.
      if (Object.keys(state.players).length === 0 && !state.lastEntry) return state;
      return withHistory(state, { ...state, players: {}, verdicts: [], lastEntry: null });

    case 'undo': {
      if (state.past.length === 0) return state;
      const past = state.past.slice(0, -1);
      return { ...state.past[state.past.length - 1], levelLocked: state.levelLocked, past };
    }

    default:
      return state;
  }
};
